import React, { type ReactNode } from 'react';
import { type ApiErrorType } from '../dm/errors.js';
import { LoadingMessage, LoadingModal } from '../common/LoadingMessage.js';
import { ErrorLoadingDataMessage } from '../common/ErrorMessage.js';

/**
 * Helper function to be called at the beginning of React components that are using RTQ to determine:
 *
 * 1. Have the API(s) completed loading?
 * 1. Were any errors returned?
 *
 * This just handles things in a common way.
 *
 * @example
 * const resp  handleInit([isLoading], [error]);
 * if ( resp ) return resp;
 *
 * @example
 * const resp = handleInit([isLoading, isOtherLoading], [error, isOtherError]);
 * if ( resp ) return resp;
 *
 * @param loadingElements List of bool `isLoading` responses from RTQ
 * @param errorObjects List of errors returned from RTQ
 * @param showAsModal Whether loading should be shown as a modal
 * @returns Either a React Node to be rendered, or undefined if everything's good to go
 */
export const handleInit = (
  loadingElements: boolean[],
  errorObjects: ApiErrorType[],
  showAsModal = false
): ReactNode | undefined => {
  if (loadingElements.includes(true)) {
    if (showAsModal) return <LoadingModal show />;

    return <LoadingMessage />;
  }

  if (errorObjects.some((eo) => eo !== undefined)) {
    return <ErrorLoadingDataMessage errors={errorObjects} />;
  }

  return undefined;
};
